import React from 'react';
import { Transaction } from '../types';
import { mockLinkedCards } from '../data/mockData';
import { CheckCircle, Clock, CreditCard } from 'lucide-react';
interface TransactionItemProps {
  transaction: Transaction;
}
export function TransactionItem({
  transaction
}: TransactionItemProps) {
  const isEarned = transaction.status === 'earned';
  // Find the card used for this transaction
  const card = transaction.cardId ? mockLinkedCards.find(c => c.id === transaction.cardId) : null;
  const brandColors = {
    visa: 'bg-blue-600',
    mastercard: 'bg-red-600',
    amex: 'bg-blue-800',
    discover: 'bg-orange-600'
  };
  const brandNames = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'Amex',
    discover: 'Discover'
  };
  return <div className="bg-white border border-gray-200 rounded-lg p-3 md:p-5 mb-3 md:mb-4 hover:bg-gray-50 transition-colors" data-id="element-168">
      {/* Mobile Layout - Vertical Stack */}
      <div className="md:hidden space-y-2" data-id="element-169">
        {/* Header: Merchant + Status Badge */}
        <div className="flex items-start justify-between gap-2" data-id="element-170">
          <div className="flex-1 min-w-0" data-id="element-171">
            <h4 className="text-base font-bold text-gray-900 truncate" data-id="element-172">
              {transaction.merchant}
            </h4>
            <p className="text-xs text-gray-500 mt-0.5" data-id="element-173">{transaction.date}</p>
          </div>
          <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${isEarned ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-174">
            {isEarned ? 'Earned' : 'Pending'}
          </span>
        </div>

        {/* Card Info */}
        {card && <div className="flex items-center gap-1.5" data-id="element-175">
            <div className={`${brandColors[card.brand]} p-0.5 rounded`} data-id="element-176">
              <CreditCard className="w-2.5 h-2.5 text-white" data-id="element-177" />
            </div>
            <span className="text-xs text-gray-600 font-medium" data-id="element-178">
              {brandNames[card.brand]} ••{card.last4}
            </span>
          </div>}

        {/* Amount Info - Horizontal on Mobile */}
        <div className="flex items-center justify-between pt-2 border-t border-gray-100" data-id="element-179">
          <div data-id="element-180">
            <span className="text-xs text-gray-500" data-id="element-181">Purchase</span>
            <p className="text-base font-semibold text-gray-900" data-id="element-182">
              ${transaction.amount.toFixed(2)}
            </p>
          </div>
          <div className="text-right" data-id="element-183">
            <span className="text-xs text-gray-500" data-id="element-184">Cash Back</span>
            <p className={`text-base font-bold ${isEarned ? 'text-green-700' : 'text-amber-600'}`} data-id="element-185">
              +${transaction.cashback.toFixed(2)}
            </p>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Horizontal */}
      <div className="hidden md:flex md:items-center justify-between" data-id="element-186">
        <div className="flex items-center gap-4" data-id="element-187">
          <div className={`p-3 rounded-full flex-shrink-0 ${isEarned ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-188">
            {isEarned ? <CheckCircle className="h-8 w-8" data-id="element-189" /> : <Clock className="h-8 w-8" data-id="element-190" />}
          </div>

          <div data-id="element-191">
            <h4 className="text-xl font-bold text-gray-900" data-id="element-192">
              {transaction.merchant}
            </h4>
            <div className="flex items-center gap-2 mt-1" data-id="element-193">
              <p className="text-gray-600 text-lg" data-id="element-194">{transaction.date}</p>
              {card && <>
                  <span className="text-gray-300" data-id="element-195">•</span>
                  <div className="flex items-center gap-1.5" data-id="element-196">
                    <div className={`${brandColors[card.brand]} p-1 rounded`} data-id="element-197">
                      <CreditCard className="w-3 h-3 text-white" data-id="element-198" />
                    </div>
                    <span className="text-gray-600 text-sm font-medium" data-id="element-199">
                      {brandNames[card.brand]} ••{card.last4}
                    </span>
                  </div>
                </>}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-8" data-id="element-200">
          <div className="text-right" data-id="element-201">
            <span className="block text-gray-500 text-base" data-id="element-202">Purchase</span>
            <span className="block text-xl font-medium text-gray-900" data-id="element-203">
              ${transaction.amount.toFixed(2)}
            </span>
          </div>

          <div className="text-right" data-id="element-204">
            <span className="block text-gray-500 text-base" data-id="element-205">Cash Back</span>
            <span className={`block text-2xl font-bold ${isEarned ? 'text-green-700' : 'text-amber-600'}`} data-id="element-206">
              +${transaction.cashback.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>;
}